import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Divider, Alert, Chip, Grid, Button } from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Calculate as CalculateIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  Help as HelpIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { getApiBaseUrlWithFallback } from '../config/environment';

export default function Rules() {
  const [helpTitle, setHelpTitle] = useState('');
  const [helpContent, setHelpContent] = useState('');
  const [helpEmail, setHelpEmail] = useState('');

  useEffect(() => {
    const loadHelpInfo = async () => {
      try {
        const baseUrl = await getApiBaseUrlWithFallback();
        // First get current season to find interclub
        const seasonsResponse = await fetch(`${baseUrl}/seasons?current=true`);
        if (!seasonsResponse.ok) return;
        
        const seasons = await seasonsResponse.json();
        const currentSeasons = seasons.filter((s) => s.isCurrent);
        if (currentSeasons.length === 0) return;
        
        const season = currentSeasons[0];
        if (!season.interclubId) return;
        
        const interclubResponse = await fetch(`${baseUrl}/interclubs/${season.interclubId}`);
        if (interclubResponse.ok) {
          const interclub = await interclubResponse.json();
          setHelpTitle(interclub?.helpTitle || '');
          setHelpContent(interclub?.helpContent || '');
          setHelpEmail(interclub?.helpEmail || interclub?.contact?.email || '');
        }
      } catch (error) {
        console.error('Error loading help info:', error);
      }
    };
    loadHelpInfo();
  }, []);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box sx={{ 
            display: 'inline-flex', 
            p: 2, 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            mb: 2,
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
          }}>
            <TrophyIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'text.primary' }}>
            Competition Rules & Regulations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete guide to interclub competition rules, scoring, and qualification
          </Typography>
        </Box>

        <Divider sx={{ mb: 5 }} />

        {/* Registration Requirements */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
            }}>
              <CheckIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Registration Requirements
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            All skaters must complete season registration and pay the entry fee before competing. 
            Unregistered skaters are not eligible for competition points or qualifiers.
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Eligibility Requirements
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>
              <Typography variant="body1">
                <strong>U.S. Figure Skating Membership:</strong> A current, valid membership is required 
                for all competitors. Membership must be active on the day of competition.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Season Registration:</strong> Skaters must register for the current season through 
                this system before their first competition.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Club Membership:</strong> Skaters must be affiliated with a member club of the 
                interclub organization.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Level Declaration:</strong> Skaters must declare their competition level at the 
                start of each season. Level changes during the season are subject to interclub rules.
              </Typography>
            </li>
          </Box>
        </Box>

        {/* Scoring System */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <CalculateIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Scoring System
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
            Points are awarded based on placement in each competition. The scoring system rewards 
            consistent performance across multiple events.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Standard Point Scale
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, justifyContent: 'center' }}>
            {/* 1st Place - Blue Ribbon */}
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                minWidth: 100,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                color: 'white',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 24px rgba(21, 101, 192, 0.4)',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 800, textShadow: '1px 1px 2px rgba(0,0,0,0.2)' }}>6</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>1st Place</Typography>
            </Paper>
            
            {/* 2nd Place - Red Ribbon */}
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                minWidth: 100,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                color: 'white',
                boxShadow: '0 6px 20px rgba(211, 47, 47, 0.35)',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 800 }}>5</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>2nd Place</Typography>
            </Paper>
            
            {/* 3rd Place - White Ribbon */}
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                minWidth: 100,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #FFFFFF 0%, #E0E0E0 100%)',
                color: '#333',
                border: '2px solid #BDBDBD',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 800 }}>4</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>3rd Place</Typography>
            </Paper>
            
            {/* 4th Place - Yellow Ribbon */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                minWidth: 100,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #FDD835 0%, #F9A825 100%)',
                color: '#333',
                boxShadow: '0 4px 16px rgba(253, 216, 53, 0.4)',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 800 }}>3</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>4th Place</Typography>
            </Paper>
            
            {/* 5th Place - Green Ribbon */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                minWidth: 100,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
                color: 'white',
                boxShadow: '0 4px 16px rgba(67, 160, 71, 0.35)',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 800 }}>2</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>5th Place</Typography>
            </Paper>
            
            {/* 6th Place - Pink Ribbon */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2.5, 
                textAlign: 'center', 
                minWidth: 100,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #EC407A 0%, #C2185B 100%)',
                color: 'white',
                boxShadow: '0 4px 16px rgba(236, 64, 122, 0.35)',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 800 }}>1</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>6th Place</Typography>
            </Paper>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', fontStyle: 'italic' }}>
            Placements beyond 6th receive 0 points
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Solo Events
          </Typography>
          <Typography variant="body1" paragraph>
            When a skater is the only competitor in their event (solo event), they receive <strong>3 points</strong> instead 
            of the standard 6 points for 1st place. All other placements receive standard points regardless of event size.
          </Typography>
        </Box>

        {/* Season Standings */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(255, 193, 7, 0.4)'
            }}>
              <TrophyIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Season Standings & Qualification
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Total Score Calculation
          </Typography>
          <Typography variant="body1" paragraph>
            A skater's total season score is calculated using their <strong>top 3 competition 
            scores</strong> (regardless of level). This encourages participation while not penalizing 
            skaters who cannot attend all events.
          </Typography>

          {/* One Competition Per Host Club Rule - Enhanced Section */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 3, 
              background: 'linear-gradient(180deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
              }}>
                <CalculateIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                One Competition Per Host Club Rule
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
              Only your <strong>best score</strong> from each host club counts toward your top 3 scores. 
              If you compete at the same club multiple times, only your highest score from that club is eligible.
            </Typography>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>
              Why This Matters
            </Typography>
            <Typography variant="body2" paragraph>
              This rule encourages skaters to participate in events hosted by different clubs throughout 
              the season, promoting broader participation across the interclub community.
            </Typography>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.secondary' }}>
              Examples
            </Typography>
            
            <Grid container spacing={2}>
              {/* Good Example */}
              <Grid item xs={12} md={6}>
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, borderRadius: 2, bgcolor: 'success.50', borderColor: 'success.main' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      Optimal Strategy
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Sarah competes at <strong>3 different clubs</strong>:
                  </Typography>
                  <Box sx={{ pl: 2, mb: 1 }}>
                    <Typography variant="body2">• Club A: 2nd place = 5 pts ✓</Typography>
                    <Typography variant="body2">• Club B: 1st place = 6 pts ✓</Typography>
                    <Typography variant="body2">• Club C: 3rd place = 4 pts ✓</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                    Total: 15 points (all 3 scores count!)
                  </Typography>
                </Paper>
              </Grid>

              {/* Scenario with Same Club */}
              <Grid item xs={12} md={6}>
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, borderRadius: 2, bgcolor: 'warning.50', borderColor: 'warning.main' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                      Same Club Scenario
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Mike competes at Club A twice:
                  </Typography>
                  <Box sx={{ pl: 2, mb: 1 }}>
                    <Typography variant="body2">• Club A (Oct): 1st place = 6 pts ✓</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                      • Club A (Nov): 2nd place = 5 pts ✗ (shadowed)
                    </Typography>
                    <Typography variant="body2">• Club B: 4th place = 3 pts ✓</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                    Total: 9 points (only 2 clubs, so only 2 scores count)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Pro tip:</strong> Use the <strong>Eligibility Tracker</strong> in your skater profile 
                to see exactly which of your scores count and plan which events to attend!
              </Typography>
            </Alert>
          </Paper>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Only competitions where the skater was properly registered 
              count toward season standings. Late registrations may result in competitions being excluded 
              from point calculations.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Championship Qualification
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1"><strong>Top 12 qualify:</strong> The top 12 skaters from each level qualify to compete in the championship round</Typography></li>
            <li><Typography variant="body1"><strong>Minimum 3 competitions:</strong> Skaters must have competed in at least 3 competitions to qualify</Typography></li>
            <li><Typography variant="body1"><strong>Qualify for highest level:</strong> Skaters qualify for their highest competition level, not their starting level</Typography></li>
            <li><Typography variant="body1"><strong>Level counting:</strong> Pre-Preliminary and Adult levels count toward the 3 competition minimum</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Tiebreakers
          </Typography>
          <Typography variant="body1" paragraph>
            When skaters have the same total points, ties are broken using the following criteria in order:
          </Typography>
          
          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Chip label="1" size="small" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 700, minWidth: 28 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Level Advancement</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Skaters who moved up levels during the season are ranked higher. More levels advanced = higher priority.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                    e.g., Aspire 1 → Aspire 2 in the same competition year
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Chip label="2" size="small" sx={{ bgcolor: '#D32F2F', color: 'white', fontWeight: 700, minWidth: 28 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Higher Individual Scores</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compare top 3 scores one by one. For example, 6-6-1 beats 6-4-3 because the second score (6) is higher than (4).
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Chip label="3" size="small" sx={{ bgcolor: '#333', color: 'white', fontWeight: 700, minWidth: 28 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Total Competitions Entered</Typography>
                  <Typography variant="body2" color="text.secondary">
                    If still tied, the skater who competed in more competitions wins.
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Chip label="4" size="small" sx={{ bgcolor: '#FDD835', color: '#333', fontWeight: 700, minWidth: 28 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Earliest High Score</Typography>
                  <Typography variant="body2" color="text.secondary">
                    If still tied, the skater who achieved their highest score first (at an earlier competition) wins.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Example:</strong> Two skaters both have 13 total points. Skater A moved up 1 level, Skater B stayed at the same level. 
              Skater A wins the tiebreaker because level advancement is the first criteria.
            </Typography>
          </Alert>
        </Box>

        {/* Level Progression */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)'
            }}>
              <StarIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Level Progression
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            Competition Levels (Basic Skills Series)
          </Typography>
          
          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
              Youth Levels
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Chip label="Snowplow Sam" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }} />
              <Chip label="Basic 1" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
              <Chip label="Basic 2" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
              <Chip label="Basic 3" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
              <Chip label="Basic 4" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
              <Chip label="Basic 5" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
              <Chip label="Basic 6" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
              <Chip label="Pre-Preliminary (Well Balanced)" sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 600 }} />
            </Box>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'secondary.main' }}>
              Adult Levels
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label="Adult 1" sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 500 }} />
              <Chip label="Adult 2" sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 500 }} />
              <Chip label="Adult 3" sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 500 }} />
              <Chip label="Adult 4" sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 500 }} />
              <Chip label="Adult 5" sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 500 }} />
              <Chip label="Adult 6" sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 500 }} />
            </Box>

            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Note about Pre-Preliminary:</strong> Our interclub series uses the <strong>Well Balanced</strong> program 
                for Pre-Preliminary skaters. Skaters who compete in <strong>Excel Series</strong> events at the Pre-Preliminary 
                level are not eligible for our interclub championship series.
              </Typography>
            </Alert>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Moving Up Levels
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>
              <Typography variant="body1">
                <strong>During Season:</strong> Coaches may move their skaters up to a higher level at any 
                time during the season. Points earned at the previous level transfer to the new level.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Starting Level:</strong> Skaters must declare their starting level at the beginning 
                of each season during registration.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Moving Down:</strong> Skaters generally cannot move down levels during a season 
                once they have competed at a higher level.
              </Typography>
            </li>
          </Box>
        </Box>

        {/* Club Rankings */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
            }}>
              <TrophyIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Club Rankings
            </Typography>
          </Box>

          <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
            Clubs are ranked by participation: 1 point per paid registered skater per eligible competition.
            Skaters who only have 1 or 2 competitions still count. Championship qualification is not required.
          </Typography>

          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>
              <Typography variant="body1">
                Each paid registered skater earns 1 club point for every eligible qualifying competition they skate
                (free skate/program; season fee paid on or before the event’s last day). Excel, showcase, and the
                championship do not count.
              </Typography>
            </li>
            <li><Typography variant="body1"><strong>Top 3 clubs receive club awards</strong> at the championship</Typography></li>
          </Box>

          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Check the Qualifiers page for current club standings.
          </Alert>
        </Box>

        {/* Code of Conduct */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(250, 112, 154, 0.3)'
            }}>
              <WarningIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Code of Conduct
            </Typography>
          </Box>

          <Typography variant="body1" paragraph>
            All participants—including skaters, coaches, and parents—are expected to maintain high 
            standards of sportsmanship and conduct.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Expected Behavior
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Treat all competitors, officials, and volunteers with respect</Typography></li>
            <li><Typography variant="body1">Follow all facility rules and safety guidelines</Typography></li>
            <li><Typography variant="body1">Accept judging decisions gracefully</Typography></li>
            <li><Typography variant="body1">Support fellow skaters regardless of club affiliation</Typography></li>
            <li><Typography variant="body1">Report any safety concerns to event officials immediately</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Prohibited Conduct
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body1">Unsportsmanlike behavior or language</Typography></li>
            <li><Typography variant="body1">Interference with other skaters during warm-up or competition</Typography></li>
            <li><Typography variant="body1">Harassment of judges, officials, or other participants</Typography></li>
            <li><Typography variant="body1">Violation of USFS SafeSport policies</Typography></li>
          </Box>

          <Alert severity="error" sx={{ mb: 3 }}>
            Violations of the code of conduct may result in point deductions, disqualification from events, 
            or suspension from the interclub.
          </Alert>
        </Box>

        {/* Contact / Help Section */}
        <Box sx={{ mb: 3 }}>
          {helpEmail ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                borderRadius: 3,
                background: 'linear-gradient(180deg, rgba(25,118,210,0.04) 0%, rgba(25,118,210,0.08) 100%)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                  <HelpIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {helpTitle || 'Need Help?'}
                </Typography>
              </Box>
              
              {helpContent && (
                <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
                  {helpContent}
                </Typography>
              )}
              
              {!helpContent && (
                <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
                  Have questions about rules, scoring, or eligibility? 
                  We're here to help! Contact our support team and we'll get back to you as soon as possible.
                </Typography>
              )}
              
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<EmailIcon />}
                href={`mailto:${helpEmail}`}
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 999, 
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                  '&:hover': { 
                    background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                  }
                }}
              >
                Contact Us
              </Button>
            </Paper>
          ) : (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Questions?
              </Typography>
              <Typography variant="body1" paragraph>
                For questions about rules, scoring, or eligibility, please contact your club's interclub 
                representative or the interclub administrator.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Note: Specific rules may vary by interclub. This guide provides general information applicable 
                to most interclub organizations. Always check with your specific interclub for any variations 
                or additional rules.
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
